import type { ReactNode } from "react";
import { Button } from "./Button";
import { Modal } from "./Modal";
import { AlertIcon } from "@/components/icons";

/**
 * "Are you sure?", for the four things in this app that cannot be undone.
 *
 * **A destructive action gets a dialog, not a second click.** The pattern this
 * replaces — a trash button that arms itself and deletes on the next press —
 * fails in the two ways that matter: it never says *what* is about to go, and
 * on a list of near-identical rows the armed one is a colour change most
 * people do not read before clicking again. It also cannot say what goes with
 * it, and everything here has children: a report owns its tiles, a dashboard
 * owns its tiles, a conversation owns its answers and its trace.
 *
 * The native `<dialog>` underneath brings the focus trap and Escape (see
 * `Modal`). Focus lands on **Cancel**, so the keyboard default is to keep
 * things: a stray Return on a dialog that appeared while somebody was typing
 * must not be the thing that deletes their work.
 *
 * Failure is not this component's job. The mutation cache announces it
 * (backlog item 11); what this owes the caller is to stay open while the
 * request is in flight and to close only when the caller says so.
 */
export function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  title,
  description,
  consequence,
  confirmLabel = "Delete",
  loading = false,
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  /** What is being deleted, named. */
  description: ReactNode;
  /** What goes with it — tiles, answers, history. Rendered as the warning. */
  consequence?: ReactNode;
  confirmLabel?: string;
  loading?: boolean;
}) {
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      footer={
        <>
          <Button autoFocus onClick={onClose}>
            Cancel
          </Button>
          <Button variant="danger" loading={loading} onClick={onConfirm}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <p className="type-body text-ink">{description}</p>
        {consequence ? (
          <p className="bg-error-subtle text-error type-support rounded-element flex items-start gap-2 px-3 py-2">
            <AlertIcon className="mt-0.5 size-3.5 shrink-0" />
            <span>{consequence}</span>
          </p>
        ) : null}
      </div>
    </Modal>
  );
}
